import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { timeout } from '@finder/shared/utils/time/timeout';
import { FLIP_CLOCK_ANIMATIONS } from './flip-clock.animation';
import { FlipClock, EasterEggs, FlipClockMedia, FlipClockCopy } from './flip-clock.interface';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CommonModule } from '@angular/common';
import { PictureComponent } from '@finder/shared/components/picture/picture.component';
import { LottieComponent } from '@finder/shared/components/lottie/lottie.component';
import { ComponentHeaderComponent } from '@finder/shared/components/component-header/component-header.component';
import { CountdownFlipClockComponent } from '@finder/shared/components/countdown-flip-clock/countdown-flip-clock.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';

@Component({
    selector: 'finder-flip-clock',
    templateUrl: './flip-clock.component.html',
    styleUrls: ['./flip-clock.component.scss'],
    animations: FLIP_CLOCK_ANIMATIONS,
    standalone: true,
    imports: [
        CommonModule,
        PictureComponent,
        LottieComponent,
        ComponentHeaderComponent,
        CountdownFlipClockComponent,
        SafeHtmlPipe,
    ],
})
export class FlipClockComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() data: FlipClock;
    @Output() removeFlipClock = new EventEmitter<any>();

    ariaLabel: string;
    initialValues: any;
    mobileCopy = [];

    copy: FlipClockCopy;
    media: FlipClockMedia;
    easterEggs: EasterEggs;
    wrappers: Array<HTMLElement>;


    // Flags
    loaded = false;
    isLACD = false;
    endState = false;
    isMobile = false;
    secondClock = false;
    validTargetDay = false;

    // Animation states
    introState = '';
    outroState = '';

    private destroy$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        private localeService: LocaleService,
        private viewportService: ViewportDetectionService
    ) {}

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit() {
        this.isMobile = this.viewportService.isMobile();
        this.isLACD = this.localeService.isLACDRegion();

        this.initializeCLock();
        this.setMobileCopy();
        this.checkTargetDay();
    }

    ngAfterViewInit() {
        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isMobile = this.viewportService.isMobile();
            });
    }

    initializeCLock() {
        this.copy = this.data?.copy;
        this.media = this.data?.media;
        this.easterEggs = this.data?.easterEggs;
    }

    onUpdate(e) {
        if (!this.loaded) {
            this.setRevealedState(e);
            this.ariaLabel = this.getAriaClock();
        }

        if (!this.easterEggs) {
            return;
        }

        timeout(() => {
            this.updateZeroes(e);
        }, 100);
    }

    onEnded(value) {
        // Update Aria Label text right after the countdown ends.
        this.ariaLabel =
            `${this.copy.title} ${this.copy.subtitle}. ${this.copy.description} ${this.media?.bottomImage?.alt ?? ''}`;

        // Remove the entire module after the countdown ends. This means: no end state nor transitions.
        const removeModuleAfterCountdown = this.data.config?.removeModuleAfterCountdown;
        if (removeModuleAfterCountdown) {
            this.removeFlipClock.emit(true);

            return;
        }

        // Remove the 3,2,1 countdown. Go straight to the end-state.
        const disableFinalCountdown = this.data.config?.disableFinalCountdown;
        if (!disableFinalCountdown && !this.secondClock) {
            this.setFinalCountdown();

            return;
        }

        // Display the end state animation.
        /* istanbul ignore else */
        if (this.secondClock || disableFinalCountdown) {

            // Fade-out flip-clock, and Intro lotties.
            // Fade-in Outro lotties.
            this.introState = 'fade-out';
            this.outroState = 'fade-in';

            // Wait for 1 sec for the fade-out to complete.
            timeout(() => {
                this.endState = value;
            }, 1000);

            this.showTransition();
        }
    }

    /**
     * @name           showTransition
     * @description    Wait for X miliseconds for the transition animation to run,
     *                 then allow the Welcome message to fade-in.
     */
    showTransition() {
        const transitionDuration = this.data.config?.transitionDuration ?? 2000;
        timeout(() => {
            const message = this.getChildElement('.end-state-message');
            message?.classList.add('revealed');
        }, transitionDuration);
    }

    /**
     * @name            setFinalCountdown
     * @description     Set the 3,2,1 countdown if enabled.
     */
    setFinalCountdown() {
        // When the original countdown ends,
        // we create a second clock with a 3 seconds countdown.
        if (!this.secondClock && !this.validTargetDay) {
            const date = new Date();
            date.setSeconds(date.getSeconds() + 4);
            this.data.config.value = date.toISOString();
            this.secondClock = true;

            return;
        }
    }

    /**
     * @name            checkTargetDay
     * @description     Check if the target date is valid or has expired.
     */
    checkTargetDay() {
        const currentDate = new Date();
        const targetDate = new Date(this.data.config.value);
        this.validTargetDay = targetDate.getTime() > currentDate.getTime();
    }

    /**
     * @name          setRevealedState
     * @description   When the clock first loads we need to check
     *                what zeros are showing, and set the revealed state
     *                to avoid showing the Easter Egg.
     * @param         value Initial Value.
     */
    setRevealedState(value) {
        // establish the countdown values at time of page load so aria experience can be set
        this.initialValues = this.getTimeValues(value);

        this.loaded = true;

        if (!this.easterEggs) {
            return;
        }

        Object.keys(this.easterEggs).forEach((key, index) => {
            const group = this.easterEggs[key];
            if (/0$/i.test(value[index])) {
                group.right.revealed = true;
            }

            if (value[index] < 10) {
                group.left.revealed = true;
            }
        });
    }

    /**
     * @name        setMobileCopy
     * @description For mobile view we need to split the CC header
     *              and display each piece on a different line.
     */
    setMobileCopy() {
        const copy = this.data.copy;
        if (!copy.mobileHeader) {
            return;
        }
        const rgx = RegExp(copy.mobileHeader, 'gi');
        this.mobileCopy = copy.subtitle.split(rgx);
    }

    /**
     * @name            getAriaClock
     * @description     Generate a string to be read aloud by screen reader
     */
    getAriaClock() {
        // For readability.
        let {days, hours, minutes } = this.initialValues;

        const labels = this.data.config.labels;
        const a11yNote = this.copy?.a11yNote ?? '';
        const bottomText = this.media?.bottomImage?.alt ?? '';
        const countdownTitle = this.data?.headerData?.title ?? '';
        const countdownSubtitle = this.data?.headerData?.subtitle ?? '';

        days = days ? `${days} ${days < 2 ? labels.DAY_SINGULAR : labels.DAY_PLURAL}` : '';
        hours = hours ? `, ${hours} ${hours < 2 ? labels.HOUR_SINGULAR : labels.HOUR_PLURAL}` : '';
        minutes = minutes ? `, ${minutes} ${minutes < 2 ? labels.MINUTE_SINGULAR : labels.MINUTE_PLURAL}.`: '';

        const ariaParts = [countdownTitle, countdownSubtitle, a11yNote, days, hours, minutes, bottomText];

        return ariaParts.join(' ').trim().toLocaleLowerCase();
    }

    /**
     * @name        updateZeroes
     * @description Check the Easter Eggs every time the clock ticks.
     * @param       value Current time.
     */
    updateZeroes(value) {
        const time = this.getTimeValues(value);

        // Get All the spans the are used to build the digits.
        this.wrappers = this.getWrappers();

        this.checkEasterEgg(time.days, 'days', 'parks');
        this.checkEasterEgg(time.hours, 'hours', 'mickey');
        this.checkEasterEgg(time.minutes, 'minutes', 'mickey');
        this.checkEasterEgg(time.seconds, 'seconds', 'mickey');
    }

    /**
     * @name            checkEasterEgg
     * @description     Reveal Easter Egg and set the revealed state
     *                  so it doesn't show multiple times.
     * @param           value Days | Hours | Minutes | Seconds
     * @param           groupName Group Name
     * @param           icon
     */
    checkEasterEgg(value, groupName, icon) {
        const { right, left } = this.easterEggs[groupName];

        // Check for Zero on the right.
        /* istanbul ignore else */
        if (/0$/i.test(value) && !right.revealed) {
            this.egg(icon, right);
            this.eggInterval(right);
            right.revealed = true;
        } else if (!/0$/i.test(value)) {
            right.revealed = false;
        }

        // Check for Zero on the left.
        if (value === 9 && !left.revealed) {
            this.egg(icon, left);
            this.eggInterval(left);
            left.revealed = true;
        }
    }

    getTimeValues(values) {
        return {
            days: values[0],
            hours: values[1],
            minutes: values[2],
            seconds: values[3],
        };
    }

    getWrappers() {
        return this.elementRef.nativeElement.getElementsByClassName('tick-flip-panel-text-wrapper');
    }

    egg(name, digit) {
        this.wrappers[digit.top].innerHTML = this.getImage(name, 'top');
        this.wrappers[digit.bottom].innerHTML = this.getImage(name, 'bottom');
    }

    eggInterval(digit) {
        timeout(() => {
            this.wrappers[digit.top].innerHTML = '0';
            this.wrappers[digit.bottom].innerHTML = '0';
        }, 1000);
    }

    getImage(name, pos) {
        // Random Easter Eggs for the Day Count.
        const icons = this.data?.icons;
        const index = Math.floor((Math.random() * 4));
        const src = (name === 'parks') ? icons[name][index] : icons[name];
        // Extra Class for Magic Kingdom since the icon has different dimensions.
        const classname = src.includes('50th_mk_icon') ? 'mk' : '';

        return `<img class="easter-egg ${name} ${pos} ${classname}" src="${src}" />`;
    }

    getChildElement(className) {
        return this.elementRef.nativeElement.querySelector(className);
    }
}
